import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { ScreenComponent } from 'new/routes/RouterManager';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_MONITOR } from 'new/constants/pathnames';
import { Stack } from 'new/elements/Stack';
import styles from 'new/components/monitor/ErrorData/ErrorData.module.scss';
import { ResourceSearch } from 'new/elements/ResourceSearch';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_RESOURCE_QUARANTINE_FILES_REQUEST,
  GET_RESOURCE_QUARANTINE_SAMPLES_REQUEST,
} from 'js/constants/actionTypes';
import { setContextualSupport, unsetContextualSupport } from 'js/actions';
import { SUPPORT_KEY_QUARANTINE } from 'js/constants/supportPageKeys';
import { ModalFlowPreview } from 'new/elements/ModalFlowPreview';
import QuarantineDataTab from 'js/components/elements/quarantineFiles/QuarantineDataTabContainer';
import { useDrawerContext } from 'new/components/drawer/useDrawerContext';
import { Resume } from 'new/components/monitor/ResourceTriage/components/Resume';
import { useResource } from 'new/components/monitor/ResourceTriage/useResource';
import { resourceFetchActions } from 'new/components/monitor/ResourceTriage/ResourceTriage';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import { BannerDescription } from 'new/elements/BannerDescription';
import { ModalFilePreview } from 'new/elements/ModalFilePreview';
import NoContentTextView from 'js/components/elements/NoContentTextView';
import { EmptyState } from 'new/elements/EmptyState/EmptyState';

interface ErrorDataProps {}

const description =
  'Store any record that failed to make its journey from source to destination in a file system of your choice.';

export const ErrorData: ScreenComponent<ErrorDataProps> = (props) => {
  const history = useHistory();
  const { drawerContentRef } = useDrawerContext();
  const [requested, setRequested] = useState(false);
  const [preventRequest, setPrevent] = useState(true);
  const initialParams = new URLSearchParams(history.location.search);
  const [data, setData] = useState({
    type: initialParams.get('type'),
    id: initialParams.get('id'),
  });
  const dispatch = useDispatch();
  const loadingQuarantine = useIsLoading(
    GET_RESOURCE_QUARANTINE_FILES_REQUEST,
    GET_RESOURCE_QUARANTINE_SAMPLES_REQUEST,
  );
  const { resource, loading: loadingResource } = useResource(data.type, data.id);
  const loading = loadingQuarantine || loadingResource;

  useEffect(() => {
    dispatch(setContextualSupport(SUPPORT_KEY_QUARANTINE));
    onSearch(data);
    return function cleanup() {
      setRequested(false);
      dispatch(unsetContextualSupport(SUPPORT_KEY_QUARANTINE));
    };
  }, []);

  const updateUrl = (data) => {
    const params = new URLSearchParams(history.location.search);
    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (value) {
        params.set(key, typeof value === 'object' ? value.id : value);
      }
    });
    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  };

  const onSearch = (data) => {
    setData(data);
    setPrevent(true);
    if (data.type) {
      setRequested(true);
      const action = resourceFetchActions[data.type];
      if (action) {
        dispatch(
          action(data.id, {
            aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
            ignoreErrors: true,
          }),
        ).then(() => {
          setPrevent(false);
        });
      }
    }

    updateUrl(data);
  };

  const renderContent = () => {
    let content = null;

    if (requested && !resource) {
      content = <NoContentTextView title="Please select a valid resource to check for errors" />;
    }

    if (!requested && !resource) {
      content = (
        <EmptyState
          title="Find a Resource to Begin"
          description="Select a Resource Type and enter a Resource ID to view records that failed during Flow execution."
          tip={
            <>
              Need help finding a Resource ID?{' '}
              <Link to="/integrate/flows" target="_blank">
                Check your Data Flows
              </Link>{' '}
              (opens in new tab) or ask your admin.
            </>
          }
        />
      );
    }

    if (resource) {
      content = (
        <QuarantineDataTab
          preventRequest={preventRequest}
          parentScrollRef={drawerContentRef}
          resourceType={data.type}
          data={data}
        />
      );
    }

    return <Stack className={styles.content}>{content}</Stack>;
  };

  return (
    <>
      <BannerDescription title="Error Data" description={description} />
      <Stack flex={1} height="100%">
        <div className={styles.top}>
          <Stack className={styles.topContent}>
            <Stack
              direction="row"
              paddingRight="32px"
              justifyContent="space-between"
              alignItems="baseline"
            >
              <ResourceSearch
                onSearch={onSearch}
                noRequest
                hideOptions={['flows', 'data_schemas', 'credentials', 'data_maps']}
                type={initialParams?.get('type') || ''}
                id={initialParams?.get('id') || ''}
              />
            </Stack>
            {loading ? <CenteredLoader absolute /> : null}
          </Stack>
        </div>
        <Stack direction="row" flex={1}>
          {renderContent()}
          <Stack width="300px" minWidth="300px">
            {!!resource || loading ? (
              <Resume
                {...resource}
                loading={loading}
                name={resource?.name}
                resourceType={data.type}
                id={data.id}
                disableFlowSelection
              />
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};

ErrorData.screenConfig = {
  path: `${ROUTE_REDESIGN}${ROUTE_MONITOR}/error-data`,
  domain: 'monitor',
  header: {
    title: 'Error Data',
    slugTitle: 'Error Data',
  },
  screenName: 'ErrorData',
  modals: [ModalFlowPreview, ModalFilePreview],
};
